import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogTitle, Typography, Box } from "@mui/material";
import { DialogContent } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Chip } from "@mui/material";
import { CreateRedirectErrors, ErrorPathProps } from "./constants";
import { useRedirectsDialog } from ".";
import { useDispatch } from "react-redux";
import { notify } from "../../../../../../shell/store/notifications";
type ErrorDialogProps = {
  open: boolean;
  onClose: () => void;
  data: CreateRedirectErrors;
};
export const ErrorDialog: FC<ErrorDialogProps> = ({
  open,
  onClose,
  data
}) => {
  const dispatch = useDispatch();
  const {
    t
  } = useTranslation();
  const [errorPaths, setErrorPaths] = useState<ErrorPathProps[]>([]);
  const {
    createRedirects,
    updateRedirect,
    closeErrorDialog,
    isLoading
  } = useRedirectsDialog();
  const handleResubmit = useCallback(async () => {
    const isEdit = !!data?.ZUID;
    const paths = errorPaths?.map(error => error?.path?.trim());
    let response = null;
    if (isEdit) {
      response = await updateRedirect({
        path: paths[0],
        targetType: data?.targetType,
        code: data?.code,
        target: data?.target,
        ZUID: data?.ZUID
      });
    } else {
      response = await createRedirects({
        paths: paths,
        target: data?.target,
        targetType: data?.targetType,
        code: data?.code
      });
    }
    const errorPathRes = response?.filter((item: any) => item?.status === "error").map((item: any) => item?.path?.trim());
    if (!errorPathRes?.length) {
      dispatch(notify({
        kind: "success",
        message: !isEdit ? t("seo.errorDialogNotifyCreated", {
          count: paths?.length
        }) : t("seo.errorDialogNotifySaved", {
          path: paths[0]
        })
      }));
      closeErrorDialog();
      return;
    }
    const updatedErrorPaths = errorPaths?.filter(error => errorPathRes?.includes(error?.path?.trim()));
    setErrorPaths(updatedErrorPaths);
  }, [data, errorPaths, t]);
  useEffect(() => {
    const pathErrors = data?.errors?.map(error => {
      return {
        error: error?.error,
        path: error?.path?.trim()
      };
    });
    setErrorPaths(pathErrors);
  }, [data]);
  return <>
      <Dialog data-cy="RedirectsErrorDialog" open={open} fullWidth maxWidth={false} onClose={onClose} slotProps={{
      paper: {
        sx: {
          width: "480px"
        }
      }
    }} data-sentry-element="Dialog" data-sentry-source-file="ErrorDialog.tsx">
        <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="ErrorDialog.tsx">
          <Box sx={{
          backgroundColor: "red.100",
          borderRadius: "100%",
          width: "40px",
          height: "40px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 1.5
        }} data-sentry-element="Box" data-sentry-source-file="ErrorDialog.tsx">
            <WarningAmberRoundedIcon color="error" data-sentry-element="WarningAmberRoundedIcon" data-sentry-source-file="ErrorDialog.tsx" />
          </Box>

          <Typography variant="inherit" fontWeight={700} flexGrow={0} flexShrink={0} data-cy="RedirectsErrorDialogHeader" data-sentry-element="Typography" data-sentry-source-file="ErrorDialog.tsx">
            {t("seo.errorDialogTitle", {
            count: errorPaths?.length
          })}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{
          mt: "8px"
        }} data-sentry-element="Typography" data-sentry-source-file="ErrorDialog.tsx">
            {t("seo.errorDialogBody")}
          </Typography>
        </DialogTitle>
        <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="ErrorDialog.tsx">
          <Box data-cy="RedirectsErrorDialogListContainer" sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "stretch",
          rowGap: "20px"
        }} data-sentry-element="Box" data-sentry-source-file="ErrorDialog.tsx">
            {errorPaths?.map(error => <Box className="RedirectsErrorListItem" key={error?.path} display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" columnGap={1} width="100%">
                <Typography variant="body2" color="info.dark" flexGrow={1}>
                  {error?.path}
                </Typography>
                <Chip size="small" label={error.error} color={error?.error !== "error" ? "warning" : "error"} sx={{
              flexGrow: 0
            }} />
              </Box>)}
          </Box>
        </DialogContent>
        <DialogActions sx={{
        p: "20px"
      }} data-sentry-element="DialogActions" data-sentry-source-file="ErrorDialog.tsx">
          <Button data-cy="RedirectsErrorDialogTryAgainButton" variant="text" color="inherit" size="medium" loading={isLoading} onClick={handleResubmit} data-sentry-element="Button" data-sentry-source-file="ErrorDialog.tsx">
            {t("shell.integrationTryAgain")}
          </Button>
          <Button data-cy="RedirectsErrorDialogDoneButton" variant="contained" color="primary" size="medium" onClick={onClose} data-sentry-element="Button" data-sentry-source-file="ErrorDialog.tsx">
            {t("common.done")}
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};
export default ErrorDialog;